import {inject, Injectable, Signal, signal, WritableSignal} from '@angular/core';
import {AppStorageIdEnum} from '../../enums/app-storage-id.enum';
import {AmagnoStorageKeyEnum} from '../../enums/amagno-storage-key.enum';
import {AppStorage} from '../../models/app-storage.model';
import {AppStorageService} from '../app-storage/app-storage.service';

@Injectable({ providedIn: 'root' })
export class RecentTagValuesService {
    private readonly MAX_COUNT = 10;
    private readonly valuesByTagDefinitionId = new Map<string, WritableSignal<Array<string>>>();
    private readonly appStorageService = inject(AppStorageService);
    private readonly storage: AppStorage = this.appStorageService.getStorage(AppStorageIdEnum.AMAGNO);

    public addValue(tagDefinitionId: string, value: string): void {
        if (!value.trim()) {
            return;
        }

        const recentValues = this.getOrCreateRecentValuesSignal(tagDefinitionId);
        recentValues.update(current => {
            const deduped = current.filter(v => v !== value);
            return [
                value,
                ...deduped
            ].slice(0, this.MAX_COUNT);
        });

        this.persistCurrentValues();
    }

    public getValuesByTagDefinitionIdSignal(tagDefinitionId: string): Signal<Array<string>> {
        return this.getOrCreateRecentValuesSignal(tagDefinitionId)
            .asReadonly();
    }

    public clear(): void {
        for (const recentValues of this.valuesByTagDefinitionId.values()) {
            recentValues.set([]);
        }

        this.storage.delete(AmagnoStorageKeyEnum.RECENT_TAG_VALUES);
    }

    private getOrCreateRecentValuesSignal(tagDefinitionId: string): WritableSignal<Array<string>> {
        if (!this.valuesByTagDefinitionId.has(tagDefinitionId)) {
            const storedRecentValues = this.storage.get<Partial<Record<string, Array<string>>>>(AmagnoStorageKeyEnum.RECENT_TAG_VALUES) ?? {};
            this.valuesByTagDefinitionId.set(tagDefinitionId, signal(storedRecentValues[tagDefinitionId] ?? []));
        }

        return this.valuesByTagDefinitionId.get(tagDefinitionId)!;
    }

    private persistCurrentValues(): void {
        const allValues: Partial<Record<string, Array<string>>> = {};
        for (const [tagDefinitionId, recentValues] of this.valuesByTagDefinitionId) {
            allValues[tagDefinitionId] = recentValues();
        }

        this.storage.set(AmagnoStorageKeyEnum.RECENT_TAG_VALUES, allValues);
    }
}
